import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import DateField from "@/components/DateField";
import { useAuth } from "@/context/AuthContext";
import { exportFilename } from "@/lib/timefmt";
import { toast } from "sonner";
import { Download } from "lucide-react";

export default function AdminExportTab() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [allData, setAllData] = useState(true);
  const [kind, setKind] = useState("entries");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    if (!companyId) { toast.error("Pick a company"); return; }
    if (!allData && (!fromDate || !toDate)) { toast.error("Pick a date range"); return; }
    setBusy(true);
    try {
      const isOverview = kind === "overview";
      const params = { company_id: companyId };
      if (isOverview) {
        if (!allData) { params.from_date = fromDate; params.to_date = toDate; }
      } else {
        params.all_data = allData;
        if (!allData) { params.from_date = fromDate; params.to_date = toDate; }
      }
      const res = await api.get(isOverview ? "/admin/export/team-overview" : "/admin/export", { params, responseType: "blob" });
      const count = res.headers["x-export-count"];
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = isOverview ? exportFilename("crew-ready-team-overview", "csv", user) : exportFilename("crew-ready-company-export", "csv", user);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(isOverview ? `Exported overview for ${count ?? "?"} members` : `Exported ${count ?? "?"} entries`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl space-y-5">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 space-y-4" data-testid="admin-export-form">
        <div>
          <div className="font-heading text-lg font-semibold">Company Data Export</div>
          <p className="text-xs text-slate-500 mt-1">
            Export any or all company entries. Admin exports do <span className="text-slate-300">not</span> change the
            members&apos; &quot;already exported&quot; flags. Every export is recorded in the Export Log.
          </p>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="admin-export-company"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Export type</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="admin-export-kind"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="entries">Detailed entries (one row per entry)</option>
            <option value="overview">Team overview (one row per member)</option>
          </select>
        </div>
        <label className="flex items-center gap-2.5 text-sm">
          <input type="checkbox" checked={allData} onChange={(e) => setAllData(e.target.checked)}
            data-testid="admin-export-all" className="accent-blue-600 h-4 w-4" />
          {kind === "overview" ? "All data (duty-miss window defaults to the last 30 days)" : "All data (ignore date range)"}
        </label>
        {!allData && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">From</label>
              <DateField value={fromDate} onChange={setFromDate} testId="admin-export-from" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">To</label>
              <DateField value={toDate} onChange={setToDate} testId="admin-export-to" />
            </div>
          </div>
        )}
        <button onClick={download} disabled={busy} data-testid="admin-export-btn"
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60">
          <Download className="h-4 w-4" /> {busy ? "Preparing…" : kind === "overview" ? "Download Team Overview CSV" : "Download Company CSV"}
        </button>
      </div>
    </div>
  );
}
